import React, { useState, useEffect, useRef } from 'react';
import { ShieldCheck, X, Check, AlertCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

export default function PayPalModal({
  isOpen,
  onClose,
  amountInINR,
  description,
  onSuccess
}) {
  // 'idle' | 'loading' | 'paying' | 'verifying' | 'success' | 'failed'
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const buttonsRendered = useRef(false);

  // Helper to dynamically load script
  const loadPayPalScript = () => {
    return new Promise((resolve) => {
      if (window.paypal) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      const clientId = 'BAAalCeUphZ2Mwkd-BC'; // PayPal client ID
      script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD`;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const initiatePayment = async () => {
    try {
      setStatus('loading');
      setErrorMsg('');

      // 1. Load PayPal SDK
      const scriptLoaded = await loadPayPalScript();
      if (!scriptLoaded) {
        setStatus('failed');
        setErrorMsg('Failed to load PayPal gateway. Please check your internet connection.');
        return;
      }

      setStatus('paying');
    } catch (err) {
      console.error('Initiate PayPal payment error:', err);
      setStatus('failed');
      setErrorMsg('Failed to start secure PayPal payment.');
    }
  };

  useEffect(() => {
    if (isOpen) {
      initiatePayment();
    } else {
      setStatus('idle');
      setErrorMsg('');
      buttonsRendered.current = false;
    }
  }, [isOpen, amountInINR]);

  // Render PayPal buttons once the container is ready
  useEffect(() => {
    if (status === 'paying' && window.paypal && !buttonsRendered.current) {
      const container = document.getElementById('paypal-button-container');
      if (container) {
        container.innerHTML = ''; // Clear any previous instance
        window.paypal.Buttons({
          style: {
            layout: 'vertical',
            color: 'gold',
            shape: 'rect',
            label: 'paypal'
          },
          createOrder: async function () {
            try {
              const res = await api.post('/api/saas/clients/payment/paypal/create-order', {
                amount: amountInINR
              });
              if (!res.data.success) {
                throw new Error(res.data.message || 'Failed to create PayPal order');
              }
              return res.data.orderId;
            } catch (err) {
              console.error('Create order API error:', err);
              toast.error(err.message || 'Error initializing PayPal order');
              setStatus('failed');
              setErrorMsg(err.message || 'Failed to initialize order on server.');
              throw err;
            }
          },
          onApprove: async function (data) {
            try {
              setStatus('verifying');
              const verifyRes = await api.post('/api/saas/clients/payment/paypal/capture', {
                orderId: data.orderID
              });

              if (verifyRes.data.success) {
                setStatus('success');
                toast.success('Payment completed successfully!');
                setTimeout(() => {
                  onSuccess(verifyRes.data.paymentId);
                }, 1500);
              } else {
                setStatus('failed');
                setErrorMsg('Payment verification failed.');
              }
            } catch (err) {
              console.error('Verification error:', err);
              setStatus('failed');
              setErrorMsg(err.response?.data?.message || 'Server error verifying PayPal payment.');
            }
          },
          onError: function (err) {
            console.error('PayPal checkout error:', err);
            setStatus('failed');
            setErrorMsg('An error occurred during the PayPal checkout process.');
          },
          onCancel: function () {
            setStatus('failed');
            setErrorMsg('Payment cancelled by user.');
          }
        }).render('#paypal-button-container');
        buttonsRendered.current = true;
      }
    }
  }, [status]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex items-center justify-center z-[9999] p-4 font-sans text-slate-700">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[400px] overflow-hidden border border-slate-100 flex flex-col min-h-[350px] justify-between p-6 relative animate-fadeIn">

        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            <h3 className="text-base font-extrabold text-slate-900">Secure Checkout</h3>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 bg-slate-100 hover:bg-slate-200 p-1.5 rounded-full transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body Content */}
        <div className="flex-1 flex flex-col items-center justify-center py-6 text-center space-y-4">
          {status === 'loading' && (
            <>
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
              <div>
                <p className="font-extrabold text-slate-800">Initializing PayPal</p>
                <p className="text-xs text-slate-500 mt-1">Connecting to PayPal servers, please wait...</p>
              </div>
            </>
          )}

          {status === 'paying' && (
            <div className="w-full flex flex-col items-center space-y-3">
              <div className="mb-2">
                <p className="font-extrabold text-slate-850 text-sm">Pay with PayPal</p>
                <p className="text-xs text-slate-500 mt-1">Amount: ${(amountInINR / 80).toFixed(2)} USD (~₹{amountInINR} INR)</p>
              </div>
              <div id="paypal-button-container" className="w-full max-w-[300px] min-h-[150px]"></div>
            </div>
          )}

          {status === 'verifying' && (
            <>
              <Loader2 className="w-10 h-10 text-emerald-600 animate-spin" />
              <div>
                <p className="font-extrabold text-slate-850 text-emerald-600">Capturing Payment</p>
                <p className="text-xs text-slate-500 mt-1">Completing order transaction...</p>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-12 h-12 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center text-emerald-600 shadow-md">
                <Check className="w-6 h-6 stroke-[3]" />
              </div>
              <div>
                <p className="font-extrabold text-slate-800 text-lg">Payment Successful</p>
                <p className="text-xs text-emerald-600 font-semibold mt-1">Your account is active. Redirecting...</p>
              </div>
            </>
          )}

          {status === 'failed' && (
            <>
              <div className="w-12 h-12 rounded-full bg-red-50 border-2 border-red-200 flex items-center justify-center text-red-500">
                <AlertCircle className="w-6 h-6" />
              </div>
              <div>
                <p className="font-extrabold text-slate-800">Payment Failed</p>
                <p className="text-xs text-red-500 mt-1 font-semibold max-w-[280px] leading-relaxed">
                  {errorMsg || 'Could not process transaction.'}
                </p>
              </div>
              <button
                onClick={initiatePayment}
                className="mt-2 bg-blue-600 hover:bg-blue-500 text-white font-bold px-6 py-2 rounded-xl text-xs transition-all active:scale-95 shadow-sm"
              >
                Retry Payment
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="pt-4 border-t border-slate-100 flex items-center justify-center gap-1.5 text-[9px] text-slate-400 font-bold uppercase tracking-wider">
          <ShieldCheck className="w-4 h-4 text-emerald-600" /> Powered by PayPal Secure Gateway
        </div>

      </div>
    </div>
  );
}
